import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { env } from '@/config/env';

/**
 * User context data that combines Redux store and environment variables
 */
export interface UserContextData {
  userId: string;
  serviceProviderId?: string | undefined;
  email: string;
  organizationId: string | undefined;
  organizationName?: string | undefined;
  roles: string[];
  userType?: string | undefined;
  isFromContext: boolean; // Indicates if data is from user context (true) or env variables (false)
}

/**
 * Custom hook to get user context data based on feature toggle
 * - If VITE_USE_USER_CONTEXT=true and user is logged in: Returns data from Redux store
 * - Otherwise: Returns data from environment variables (legacy behavior)
 * 
 * @returns User context data with userId, email, organizationId, roles, and source indicator
 */
export const useUserContext = (): UserContextData => {
  // Use separate selectors to ensure fresh data
  const user = useSelector((state: RootState) => state.auth.user);
  const roles = useSelector((state: RootState) => state.auth.user?.roles || []);

  if (env.useUserContext && user) {
    // Use user context from Redux store (API data)
    
    return {
      userId: user.id,
      serviceProviderId: user.serviceProviderId,
      email: user.email,
      organizationId: user.organizationId, // Use from Redux (memberships groupId)
      organizationName: user.organizationName, // Optional: Organization name fetched from User Service
      roles: roles, // Use the separate selector
      userType: user.userType,
      isFromContext: true,
    };
  }

  // Fallback to environment variables (legacy behavior)
  return {
    userId: env.defaultEntityId,
    serviceProviderId: undefined,
    email: env.defaultUserEmail,
    organizationId: env.defaultOrgId,
    roles: env.userRoles,
    userType: undefined,
    isFromContext: false,
  };
};
